import React, { useLayoutEffect, useRef, useState, useCallback } from "react";

/**
 * FitToPanel
 * ----------
 * Wraps arbitrary rich content (HTML, tables, images, iframes, etc.) and
 * makes it FILL the parent container's width, and — when the content is
 * taller than the container — scales it down uniformly so it also fits
 * vertically. Never scrolls, never clips.
 *
 * Implementation notes:
 *  - To avoid a measurement/apply feedback loop (widening the content
 *    layout changes its natural height, which changes the required
 *    scale, which changes the layout width again...) we render the
 *    children TWICE:
 *      • A hidden measurement clone laid out at `width: 100%` gives us
 *        the true natural height at the container's real width, no
 *        matter what scale is currently applied.
 *      • The visible, scaled copy uses `width: 100% / scale` and
 *        `transform: scale(s)` so its final visual box is exactly the
 *        container's full width AND its scaled height fits within the
 *        container height.
 *  - When content already fits (s = 1) no transform is applied.
 */
export default function FitToPanel({ children, className = "", testId }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [scale, setScale] = useState(1);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const availW = container.clientWidth;
    const availH = container.clientHeight;
    if (!availW || !availH) return;
    // The measurement clone is always laid out at width:100%, so its
    // scrollHeight is the true natural height at the container's real
    // width — completely independent of the visible copy's scale.
    const naturalH = measure.scrollHeight || measure.offsetHeight || 0;
    if (!naturalH) return;
    const next = Math.min(1, availH / naturalH);
    setScale((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
  }, []);

  useLayoutEffect(() => {
    recompute();
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    const ro = new ResizeObserver(() => recompute());
    ro.observe(container);
    ro.observe(measure);

    // Late-loading media (images, iframes) can change layout after mount
    // without necessarily triggering the ResizeObserver on the measure
    // root — listen to their `load` events too.
    const media = Array.from(measure.querySelectorAll("img, iframe, video"));
    const onLoad = () => recompute();
    media.forEach((el) => el.addEventListener("load", onLoad));

    window.addEventListener("resize", recompute);
    return () => {
      ro.disconnect();
      media.forEach((el) => el.removeEventListener("load", onLoad));
      window.removeEventListener("resize", recompute);
    };
  }, [recompute, children]);

  const needsScale = scale < 0.999;
  // Widen the visible layout to `100% / scale` so that after
  // `transform: scale(scale)` the final visual width equals the
  // container's full width (no dead space on the right).
  const visibleLayoutWidth = needsScale ? `${100 / scale}%` : "100%";

  return (
    <div
      ref={containerRef}
      className={`fit-to-panel ${className}`}
      data-testid={testId}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Hidden measurement clone — always laid out at the container's
          real width, invisible, and not interactive. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          visibility: "hidden",
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        {children}
      </div>
      {/* Visible copy, scaled to fit vertically while filling width. */}
      <div
        style={{
          transform: needsScale ? `scale(${scale})` : undefined,
          transformOrigin: "top left",
          width: visibleLayoutWidth,
        }}
      >
        {children}
      </div>
    </div>
  );
}
